import React from "react";
import { useData } from "../lib/store.jsx";
import { Kpi, Panel, AreaTrend, TopList, SplitBar } from "../components/ui.jsx";

const WINDOW = 30;
const COUNTRY = new Intl.DisplayNames(["en-GB"], { type: "region" });
const flag = (cc) => (/^[A-Z]{2}$/.test(cc)
  ? String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)))
  : "🌐");
const PAGE = { "/": "Home" };
const pageName = (p) => PAGE[p] || p.replace(/^\/|\/$/g, "")
  .split("/").pop().replace(/-/g, " ")
  .replace(/^./, (c) => c.toUpperCase());

export default function Website() {
  const { d } = useData();
  const daily = d.webDaily.slice(-WINDOW);
  const since = daily.length ? daily[0].date : null;
  const inWindow = (r) => since && r.date >= since;

  const sum = (k) => daily.reduce((s, r) => s + (+r[k] || 0), 0);
  const top = (dim) => {
    const acc = {};
    for (const r of d.webTop) {
      if (r.dimension !== dim || !inWindow(r)) continue;
      acc[r.key] = (acc[r.key] || 0) + r.page_views;
    }
    return Object.entries(acc).sort((a, b) => b[1] - a[1])
      .map(([key, value]) => ({ key, value }));
  };

  const pages = top("path").slice(0, 6)
    .map((r) => ({ label: pageName(r.key), sub: r.key, value: r.value }));
  // l.facebook.com, m.facebook.com and www.facebook.com are one source.
  const refAcc = {};
  for (const r of top("referrer")) {
    const label = r.key === "Direct" ? "Typed in or bookmarked"
      : r.key.replace(/^(www|l|m|lm)\./, "");
    refAcc[label] = (refAcc[label] || 0) + r.value;
  }
  const refs = Object.entries(refAcc).sort((a, b) => b[1] - a[1]).slice(0, 6)
    .map(([label, value]) => ({ label, value }));
  const countries = top("country").slice(0, 6).map((r) => ({
    label: `${flag(r.key)}  ${/^[A-Z]{2}$/.test(r.key)
      ? COUNTRY.of(r.key) : r.key}`, value: r.value }));
  const devices = top("device");
  const devTotal = devices.reduce((s, r) => s + r.value, 0) || 1;
  const mobilePct = Math.round(((devices.find((r) => r.key === "mobile")
    ?.value || 0) / devTotal) * 100);

  const trend = daily.map((r) => ({
    x: new Date(r.date + "T00:00:00Z").toLocaleDateString("en-GB",
      { day: "numeric", month: "short", timeZone: "UTC" }),
    "Page views": r.page_views, Visits: r.visits }));

  const days = daily.length;
  const span = days === 1 ? "1 day" : `${days} days`;
  const sinceText = since ? new Date(since + "T00:00:00Z")
    .toLocaleDateString("en-GB", { day: "numeric", month: "long",
      timeZone: "UTC" }) : "";

  if (!days) {
    return (
      <Panel className="s12" title="Website analytics">
        <div className="muted">No website data has arrived yet.</div>
      </Panel>
    );
  }

  return (
    <>
      <Kpi label="Page Views" value={sum("page_views")} color="#c8e64a"
        fmt={(v) => Math.round(v).toLocaleString("en-GB")}
        meta={days < WINDOW ? `since ${sinceText}` : `last ${span}`} />
      <Kpi label="Visits" value={sum("visits")} color="#f3ece2"
        fmt={(v) => Math.round(v).toLocaleString("en-GB")}
        meta="arrivals from outside the site" />
      <Kpi label="On a Phone" value={mobilePct} color="#d9b892"
        fmt={(v) => `${Math.round(v)}%`} meta="of page views" />
      <Kpi label="Threats Blocked" value={sum("threats")} color="#ec9a74"
        fmt={(v) => Math.round(v).toLocaleString("en-GB")}
        meta="stopped by Cloudflare" />

      <Panel className="s8" title="Daily traffic"
        sub="page views and visits each day">
        <AreaTrend data={trend} x="x" h={280} series={[
          { key: "Page views", name: "Page views" },
          { key: "Visits", name: "Visits" }]} />
      </Panel>
      <Panel className="s4" title="Most viewed pages">
        <TopList rows={pages} />
      </Panel>

      <Panel className="s4" title="Where visitors come from">
        <TopList rows={refs} tone="violet" />
      </Panel>
      <Panel className="s4" title="Countries">
        <TopList rows={countries} tone="pink" />
      </Panel>
      <Panel className="s4" title="Devices">
        <SplitBar rows={devices.map((r) => ({
          label: r.key.replace(/^./, (c) => c.toUpperCase()),
          value: r.value }))} />
      </Panel>
    </>
  );
}
